/**
 * Acompanhar uma atualização até ela terminar — RF-ADM-07.
 *
 * ================================================================
 * O SERVIDOR CAI NO MEIO, E ISSO É O ESPERADO
 * ================================================================
 *
 * Clicar em Atualizar avisa o Portainer, que baixa a imagem nova e reimplanta.
 * Entre o clique e o fim, o contêiner que responde esta página é derrubado e
 * substituído — as requisições falham por alguns segundos.
 *
 * Um acompanhamento ingênuo trataria a primeira falha como erro e diria
 * "atualização falhou" justamente no momento em que ela está funcionando. Por
 * isso erro de rede aqui NÃO encerra nada: ele conta como "ainda subindo".
 *
 * O que encerra é uma resposta com a versão nova. É o único sinal que prova
 * que o sistema voltou, e voltou atualizado.
 *
 * ================================================================
 * POR QUE ISTO É UMA FUNÇÃO PURA
 * ================================================================
 *
 * A regra de "quando parar" tem quatro saídas — chegou, desistiu por tempo,
 * voltou com a versão velha, cancelado — e cada uma leva a uma mensagem
 * diferente na tela. Testar isso dentro de um componente exigiria simular
 * relógio, rede e React ao mesmo tempo.
 *
 * Aqui a decisão sai de dados: o que a consulta devolveu e quanto tempo passou.
 * O componente só desenha.
 */

export type DeployProbe =
  /** O servidor respondeu e disse qual versão está rodando. */
  | { kind: 'version'; version: string }
  /** Não respondeu — está reiniciando, ou a rede oscilou. */
  | { kind: 'unreachable' };

export type DeployPhase =
  /** Ainda não caiu: continua respondendo com a versão antiga. */
  | { phase: 'iniciando'; elapsedMs: number }
  /** Parou de responder — é o meio da troca, e é sinal de progresso. */
  | { phase: 'reiniciando'; elapsedMs: number }
  /** Voltou, mas ainda na versão antiga: pode ser o segundo serviço. */
  | { phase: 'voltando'; elapsedMs: number }
  /** Terminou. */
  | { phase: 'concluido'; version: string }
  /** Passou do tempo sem confirmar. Não é o mesmo que "falhou". */
  | { phase: 'demorando'; elapsedMs: number };

/** Depois disto, para de prometer e manda conferir no Portainer. */
export const DEPLOY_TIMEOUT_MS = 5 * 60_000;

/**
 * Em que pé está a atualização.
 *
 * @param target    A versão que se espera ver no fim.
 * @param probes    O que cada consulta devolveu, em ordem.
 * @param elapsedMs Tempo desde o clique.
 */
export function deployPhase(
  target: string,
  probes: DeployProbe[],
  elapsedMs: number,
): DeployPhase {
  const last = probes[probes.length - 1];

  /**
   * A VERSÃO NOVA ENCERRA, e nada mais.
   *
   * Vem antes do tempo esgotado de propósito: se a última consulta já confirmou
   * a versão nova, dizer "está demorando" seria mentir por causa do relógio.
   */
  if (last?.kind === 'version' && last.version === target) {
    return { phase: 'concluido', version: last.version };
  }

  if (elapsedMs >= DEPLOY_TIMEOUT_MS) {
    return { phase: 'demorando', elapsedMs };
  }

  // Nunca deixou de responder ainda: o Portainer pode não ter começado.
  const caiu = probes.some((p) => p.kind === 'unreachable');

  if (!caiu) {
    return { phase: 'iniciando', elapsedMs };
  }

  /**
   * Já caiu e voltou, mas com a versão antiga.
   *
   * Acontece de verdade: são DOIS serviços na mesma imagem (aplicação e
   * agendador), e o Portainer os troca em momentos diferentes. Chamar isso de
   * erro faria a tela desistir no meio de uma atualização que vai completar.
   */
  if (last?.kind === 'version') {
    return { phase: 'voltando', elapsedMs };
  }

  return { phase: 'reiniciando', elapsedMs };
}

/** O texto que a tela mostra em cada fase. */
export function deployMessage(state: DeployPhase, target: string): string {
  switch (state.phase) {
    case 'iniciando':
      return 'Avisando o servidor…';
    case 'reiniciando':
      return 'Reiniciando com a versão nova…';
    case 'voltando':
      return 'Serviços voltando…';
    case 'concluido':
      return `Atualizado para a versão ${state.version}.`;
    case 'demorando':
      return (
        `Já se passaram ${Math.round(state.elapsedMs / 60_000)} minutos sem confirmação da versão ` +
        `${target}. A atualização pode ter continuado — confira em Portainer → Services.`
      );
  }
}

/**
 * Quanto da barra preencher, de 0 a 1.
 *
 * ================================================================
 * NÃO É PROGRESSO REAL, E NÃO FINGE SER
 * ================================================================
 *
 * Ninguém sabe quanto falta: o Portainer não informa, e o tempo depende do
 * tamanho da imagem e da rede do servidor. Uma barra que anda até 90% e trava
 * ali é mentira — o usuário lê "quase pronto" e espera parado.
 *
 * Esta anda por FASE, e não por tempo: cada etapa alcançada é um fato
 * verificado. Ela pode ficar parada na mesma marca por um minuto, e isso é
 * honesto: nada de novo aconteceu.
 */
export function deployProgress(state: DeployPhase): number {
  switch (state.phase) {
    case 'iniciando':
      return 0.15;
    case 'reiniciando':
      return 0.5;
    case 'voltando':
      return 0.8;
    case 'concluido':
      return 1;
    case 'demorando':
      return 0.8;
  }
}
